import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquarePlus, Send, Lock } from 'lucide-react';
import { api, type DiscussionPostShape, type ThreadSummaryShape } from '@/lib/api';
import { RealtimeChannel } from '@/lib/realtime';
import { useAuth } from '@/lib/auth-store';
import { cn } from '@/lib/utils';

/**
 * DiscussionsTab — Phase 4c UI for the discussion forum inside a course.
 *
 * Layout:
 *   - Header: title + "New thread" button
 *   - Body: split — thread list (left) + open thread (right) when a thread is
 *     selected, else a centered empty/new-thread prompt.
 *
 * Realtime: open thread subscribes to `/ws/discussions/:threadId`. Incoming
 * `post` events merge into the cache.
 */
export function DiscussionsTab({ token, courseId }: { token: string; courseId: string }) {
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const threadsQ = useQuery({
    queryKey: ['discussions', courseId, 'threads'],
    queryFn: () => api.discussions.listThreads(token, courseId),
    staleTime: 30_000,
    enabled: token.length > 0,
  });

  return (
    <div className="grid gap-4 md:grid-cols-[20rem_1fr]">
      <aside className="rounded-2xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border p-3">
          <h3 className="text-sm font-semibold">Threads</h3>
          <button
            type="button"
            onClick={() => setComposing(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <MessageSquarePlus className="h-3.5 w-3.5" /> New
          </button>
        </div>
        <ul className="divide-y divide-border">
          {threadsQ.isLoading ? (
            <li className="p-4 text-sm text-muted-foreground">Loading threads…</li>
          ) : (threadsQ.data?.threads ?? []).length === 0 ? (
            <li className="p-4 text-sm text-muted-foreground">No threads yet.</li>
          ) : (
            threadsQ.data?.threads.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                active={t.id === openThreadId}
                onClick={() => setOpenThreadId(t.id)}
              />
            ))
          )}
        </ul>
      </aside>

      <section className="rounded-2xl border border-border bg-card">
        {composing ? (
          <NewThreadComposer
            token={token}
            courseId={courseId}
            onClose={() => setComposing(false)}
            onCreated={(id) => {
              setComposing(false);
              setOpenThreadId(id);
            }}
          />
        ) : openThreadId ? (
          <ThreadView
            key={openThreadId}
            token={token}
            threadId={openThreadId}
            thread={threadsQ.data?.threads.find((t) => t.id === openThreadId) ?? null}
          />
        ) : (
          <div className="flex h-full min-h-64 items-center justify-center p-8 text-sm text-muted-foreground">
            Select a thread, or start a new one.
          </div>
        )}
      </section>
    </div>
  );
}

function ThreadRow({
  thread,
  active,
  onClick,
}: {
  thread: ThreadSummaryShape;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'block w-full px-4 py-3 text-left text-sm transition-colors hover:bg-secondary/50',
          active && 'bg-secondary',
        )}
      >
        <div className="flex items-center gap-1.5">
          {thread.locked && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />}
          <span className={cn('truncate font-medium', thread.pinned && 'text-accent')}>
            {thread.title}
          </span>
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {thread.postCount} {thread.postCount === 1 ? 'post' : 'posts'} ·{' '}
          {thread.lastPostAt
            ? new Date(thread.lastPostAt).toLocaleDateString()
            : new Date(thread.createdAt).toLocaleDateString()}
        </div>
      </button>
    </li>
  );
}

function NewThreadComposer({
  token,
  courseId,
  onClose,
  onCreated,
}: {
  token: string;
  courseId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const mutation = useMutation({
    mutationFn: () => api.discussions.createThread(token, { courseId, title, bodyHtml: body }),
    onSuccess: ({ id }) => {
      void queryClient.invalidateQueries({ queryKey: ['discussions', courseId, 'threads'] });
      onCreated(id);
    },
  });

  return (
    <form
      className="flex h-full flex-col p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim() || !body.trim() || mutation.isPending) return;
        void mutation.mutateAsync();
      }}
    >
      <div className="flex items-center justify-between pb-3">
        <h3 className="font-semibold">New discussion thread</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
      <input
        type="text"
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Thread title…"
        className="mb-3 rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="What's the prompt? You can paste HTML for now; rich-text editing comes later."
        rows={8}
        className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      <div className="mt-3 flex items-center justify-end gap-2">
        {mutation.isError && (
          <span className="text-xs text-destructive">Could not create thread.</span>
        )}
        <button
          type="submit"
          disabled={!title.trim() || !body.trim() || mutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {mutation.isPending ? 'Posting…' : 'Post thread'}
        </button>
      </div>
    </form>
  );
}

function ThreadView({
  token,
  threadId,
  thread,
}: {
  token: string;
  threadId: string;
  thread: ThreadSummaryShape | null;
}) {
  const { state } = useAuth();
  const myProfileId = state.status === 'authenticated' ? state.profile.id : '';
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');

  const postsQ = useQuery({
    queryKey: ['discussions', threadId, 'posts'],
    queryFn: () => api.discussions.listPosts(token, threadId),
    staleTime: 30_000,
    enabled: token.length > 0,
  });

  const sendMutation = useMutation({
    mutationFn: (bodyHtml: string) => api.discussions.createPost(token, threadId, { bodyHtml }),
    onSuccess: ({ post }) => {
      queryClient.setQueryData<{ posts: DiscussionPostShape[] }>(
        ['discussions', threadId, 'posts'],
        (prev) => mergePost(prev?.posts ?? [], post),
      );
    },
  });

  // WS subscription
  useEffect(() => {
    const channel = new RealtimeChannel(`/ws/discussions/${threadId}`, () => token);
    const unsub = channel.subscribe((event) => {
      if (event && typeof event === 'object' && (event as { event?: unknown }).event === 'post') {
        const post = (event as { post: DiscussionPostShape }).post;
        queryClient.setQueryData<{ posts: DiscussionPostShape[] }>(
          ['discussions', threadId, 'posts'],
          (prev) => mergePost(prev?.posts ?? [], post),
        );
      }
    });
    void channel.connect();
    return () => {
      unsub();
      channel.close();
    };
  }, [threadId, token, queryClient]);

  const posts = useMemo(() => postsQ.data?.posts ?? [], [postsQ.data]);
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [posts.length]);

  return (
    <div className="flex h-full min-h-96 flex-col">
      <header className="border-b border-border p-4">
        <h3 className="font-display text-lg font-semibold">{thread?.title ?? 'Thread'}</h3>
        {thread && (
          <div
            className="prose prose-sm mt-2 max-w-none text-sm text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: thread.bodyHtml }}
          />
        )}
      </header>

      <div ref={scrollerRef} className="flex-1 space-y-3 overflow-y-auto p-4">
        {postsQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading posts…</p>
        ) : posts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No replies yet.</p>
        ) : (
          posts.map((p) => (
            <article
              key={p.id}
              className={cn(
                'rounded-2xl px-3 py-2 text-sm',
                p.authorProfileId === myProfileId
                  ? 'ml-8 bg-primary/10 text-foreground'
                  : 'mr-8 bg-secondary text-foreground',
              )}
            >
              <div
                className="prose prose-sm max-w-none break-words"
                dangerouslySetInnerHTML={{ __html: p.bodyHtml }}
              />
              <div className="mt-1 text-[10px] text-muted-foreground">
                {new Date(p.createdAt).toLocaleString()}
              </div>
            </article>
          ))
        )}
      </div>

      {!thread?.locked && (
        <form
          className="flex items-end gap-2 border-t border-border p-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const text = reply.trim();
            if (!text || sendMutation.isPending) return;
            setReply('');
            try {
              await sendMutation.mutateAsync(text);
            } catch {
              setReply(text);
            }
          }}
        >
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply…"
            rows={2}
            className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <button
            type="submit"
            disabled={!reply.trim() || sendMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" /> Send
          </button>
        </form>
      )}
    </div>
  );
}

function mergePost(
  existing: DiscussionPostShape[],
  incoming: DiscussionPostShape,
): { posts: DiscussionPostShape[] } {
  if (existing.some((p) => p.id === incoming.id)) return { posts: existing };
  return { posts: [...existing, incoming] };
}
